import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ArrowUpRight, ChevronRight, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import clsx from 'clsx';
import { CHEMINS } from '../../lib/navigation/chemins';
import { entreeCourante, estActive, type Espace } from '../../lib/navigation/espaces';
import { lireMenuReplie, retenirMenuReplie, stockageLocal } from '../../lib/navigation/menuReplie';

/**
 * Un espace de l'administration : son menu latéral, son fil d'Ariane, et la
 * page. Les pages montées ici sont les pages existantes, telles quelles
 * (Lot UI 1 : aucune logique nouvelle).
 *
 * Le menu se replie sur ses icônes (voir `menuReplie.ts`) : 184 px rendus à
 * la page, pour les tableaux qui débordaient. Replié ou non, il ne change
 * qu'à partir de la largeur tablette ; en dessous, c'est le menu dépliant du
 * bouton, comme avant. Les entrées « à venir » n'ont ni icône ni lien : le
 * menu replié ne les montre pas.
 */
export default function EspaceLayout({ espace }: { espace: Espace }) {
  const { pathname } = useLocation();
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [replie, setReplie] = useState(() => lireMenuReplie(stockageLocal()));
  const courante = entreeCourante(espace, pathname);

  const basculerReplie = () => {
    const suite = !replie;
    setReplie(suite);
    retenirMenuReplie(stockageLocal(), suite);
  };

  return (
    <div className="flex flex-col gap-6 md:flex-row">
      <aside className={clsx('md:shrink-0', replie ? 'md:w-14' : 'md:w-60')}>
        <button
          type="button"
          onClick={() => setMenuOuvert((o) => !o)}
          className="mb-2 flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 md:hidden"
          aria-expanded={menuOuvert}
        >
          {espace.titre}
          <Menu className="h-4 w-4" />
        </button>

        <nav
          aria-label={`Menu de l’espace ${espace.titre}`}
          className={clsx(
            'space-y-5 rounded-xl border border-gray-200 bg-white p-3 md:sticky md:top-4 md:block',
            replie && 'md:space-y-3 md:px-1.5',
            menuOuvert ? 'block' : 'hidden',
          )}
        >
          <button
            type="button"
            onClick={basculerReplie}
            aria-expanded={!replie}
            title={replie ? 'Déplier le menu' : 'Replier le menu'}
            className={clsx(
              'hidden w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 md:flex',
              replie && 'md:justify-center md:px-0',
            )}
          >
            {replie
              ? <PanelLeftOpen className="h-4 w-4 shrink-0" />
              : <PanelLeftClose className="h-4 w-4 shrink-0" />}
            <span className={clsx(replie && 'md:sr-only')}>
              {replie ? 'Déplier le menu' : 'Replier le menu'}
            </span>
          </button>

          {espace.rubriques.map((rubrique) => (
            <div
              key={rubrique.titre}
              className={clsx(
                replie && 'md:border-t md:border-gray-100 md:pt-3',
                // Repliée, une rubrique sans entrée active ne laisserait qu'un trait et un vide.
                replie && !rubrique.entrees.some(estActive) && 'md:hidden',
              )}
            >
              <p className={clsx(
                'px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500',
                replie && 'md:hidden',
              )}>
                {rubrique.titre}
              </p>
              <ul className="space-y-0.5">
                {rubrique.entrees.map((entree) =>
                  estActive(entree) ? (
                    <li key={entree.chemin}>
                      <NavLink
                        to={entree.chemin}
                        end
                        title={infobulle(entree.libelle, entree.ailleurs, replie)}
                        onClick={() => setMenuOuvert(false)}
                        className={() =>
                          clsx(
                            'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                            replie && 'md:justify-center md:px-0',
                            courante?.entree.chemin === entree.chemin
                              ? 'bg-primary-50 font-medium text-primary-700'
                              : 'text-gray-700 hover:bg-gray-100',
                          )
                        }
                      >
                        <entree.icone className="h-4 w-4 shrink-0" />
                        {/* `sr-only` et non `hidden` : replié, le lien garde son nom pour un lecteur d'écran. */}
                        <span className={clsx('flex-1', replie && 'md:sr-only')}>{entree.libelle}</span>
                        {entree.ailleurs && (
                          <ArrowUpRight
                            className={clsx('h-3.5 w-3.5 shrink-0 text-gray-400', replie && 'md:hidden')}
                            aria-label={`dans ${entree.ailleurs}`}
                          />
                        )}
                      </NavLink>
                    </li>
                  ) : (
                    <li
                      key={entree.libelle}
                      title={entree.raison}
                      aria-disabled="true"
                      className={clsx(
                        'flex cursor-not-allowed items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-gray-400',
                        replie && 'md:hidden',
                      )}
                    >
                      <span>{entree.libelle}</span>
                      <span className="shrink-0 whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                        à venir
                      </span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <div className="min-w-0 flex-1">
        <nav aria-label="Fil d’Ariane" className="mb-4 flex flex-wrap items-center gap-1 text-sm text-gray-500">
          <Link to={CHEMINS.tableauDeBord} className="hover:text-gray-900">Tableau de bord</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={espace.accueil} className="hover:text-gray-900">{espace.titre}</Link>
          {courante && courante.entree.chemin !== espace.accueil && (
            <>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-gray-400">{courante.rubrique.titre}</span>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-gray-700">{courante.entree.libelle}</span>
            </>
          )}
        </nav>
        <Outlet />
      </div>
    </div>
  );
}

/**
 * L'infobulle d'une entrée. Dépliée, le libellé est écrit : seule l'entrée qui
 * mène ailleurs en a besoin. Repliée, l'icône seule ne dit rien : le libellé
 * revient au survol.
 */
function infobulle(libelle: string, ailleurs: string | undefined, replie: boolean): string | undefined {
  if (replie) return ailleurs ? `${libelle} — ouvre ${ailleurs}` : libelle;
  return ailleurs ? `Ouvre ${ailleurs}` : undefined;
}
